import React, { useState, useEffect, useCallback } from "react";
import {
  getFlaggedSessions,
  getSessionDetails,
  reviewSession,
  bulkReviewSessions,
  getValidationStats,
} from "../../services/antiFraudService";
import {
  getAllPayouts,
  approvePayout,
  rejectPayout,
} from "../../services/payoutService";
import { generateBuildReport } from "../../services/developerService";
import { getPendingAssignmentQueue } from "../../services/adminAssignmentService";
import ManualAssignment from "./ManualAssignment";
import GameAssignmentsManager from "./GameAssignmentsManager";
import AdminLayout from "./AdminLayout";
import AdminDeveloperStudios from "./AdminDeveloperStudios";
import SystemHealth from "./SystemHealth";
import EvidenceSampleStrip from "./EvidenceSampleStrip";
import "./AdminDashboard.scss";

function getViewerRole() {
  try {
    const stored = JSON.parse(localStorage.getItem("userData") || "{}");
    if (stored.role) return stored.role;
  } catch {
    // ignore malformed local storage
  }
  try {
    const token = localStorage.getItem("token") || "";
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return payload.role || payload.userRole || "admin";
  } catch {
    return "admin";
  }
}

/**
 * Unified Admin Dashboard Component
 * Combines Session Review, Payout Management, and Manual Playtester Assignment
 */
const AdminDashboard = () => {
  const viewerRole = getViewerRole();
  const [activeTab, setActiveTab] = useState(viewerRole === "developer" ? "health" : "sessions");
  
  // Session Review State
  const [sessions, setSessions] = useState([]);
  const [sessionStats, setSessionStats] = useState(null);
  const [sessionFilter, setSessionFilter] = useState('flagged'); // 'flagged', 'rejected', 'approved', 'pending', 'all'
  const [selectedSession, setSelectedSession] = useState(null);
  const [reviewNotes, setReviewNotes] = useState("");
  const [fraudReason, setFraudReason] = useState("");
  const [selectedSessions, setSelectedSessions] = useState([]);
  
  // Payout Management State
  const [payouts, setPayouts] = useState([]);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [payoutFilter, setPayoutFilter] = useState('pending_approval');
  const [payoutNotes, setPayoutNotes] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  
  // Report Generation State
  const [reportDeveloperId, setReportDeveloperId] = useState("");
  const [reportGameId, setReportGameId] = useState("");
  const [reportBuildId, setReportBuildId] = useState("");
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [reportResult, setReportResult] = useState(null);
  const [reportError, setReportError] = useState(null);
  
  // Common State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pendingGames, setPendingGames] = useState([]);

  const fetchPendingAssignments = useCallback(async () => {
    try {
      const data = await getPendingAssignmentQueue();
      setPendingGames(data.games || []);
    } catch (err) {
      console.error("Error fetching pending assignment queue:", err);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'sessions') {
      fetchSessionData();
    } else if (activeTab === 'payouts') {
      fetchPayoutData();
    }
    // assignments and reports tabs handle their own data fetching
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, payoutFilter, sessionFilter]);

  useEffect(() => {
    if (viewerRole !== "admin") return;
    fetchPendingAssignments();
  }, [activeTab, fetchPendingAssignments, viewerRole]);

  // ============================================
  // SESSION REVIEW FUNCTIONS
  // ============================================

  const fetchSessionData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch sessions based on selected filter
      const queryOptions = { limit: 50, offset: 0 };
      
      // Backend endpoint expects specific validationStatus values
      // For 'approved', we might need to handle both 'approved' and 'auto_approved'
      // For 'all', we don't pass validationStatus to get all sessions
      if (sessionFilter === 'flagged') {
        queryOptions.validationStatus = 'flagged';
      } else if (sessionFilter === 'rejected') {
        queryOptions.validationStatus = 'rejected';
      } else if (sessionFilter === 'approved') {
        queryOptions.validationStatus = 'approved';
      } else if (sessionFilter === 'pending') {
        queryOptions.validationStatus = 'pending';
      }
      // For 'all', don't add validationStatus - backend returns all

      console.log('Fetching sessions with filter:', sessionFilter, 'queryOptions:', queryOptions);

      const [sessionsData, statsData] = await Promise.all([
        getFlaggedSessions(queryOptions),
        getValidationStats(),
      ]);

      console.log('Sessions data received:', sessionsData);
      setSessions(sessionsData.sessions || []);
      setSessionStats(statsData.overview || null);
    } catch (err) {
      console.error("Error fetching session data:", err);
      
      if (err.response?.status === 500 || err.code === 'ERR_BAD_RESPONSE') {
        setError("⚠️ Backend anti-fraud endpoints are not yet implemented.");
        setSessions([]);
        setSessionStats({
          pending: 0,
          auto_approved: 0,
          approved: 0,
          flagged: 0,
          rejected: 0,
          total: 0,
        });
      } else {
        setError("Failed to load session data");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSession = async (session) => {
    try {
      setLoading(true);
      const details = await getSessionDetails(session.sessionId);
      setSelectedSession({
        ...session,
        ...details,
        playtesterName: details.playtesterName || details.playtester?.name || session.playtesterName,
        playtesterUsername: details.playtesterUsername || details.playtester?.username || session.playtesterUsername,
        playtesterEmail: details.playtesterEmail || details.playtester?.email || session.playtesterEmail,
        playtesterId: details.playtesterId || details.playtester?.id || session.playtesterId,
        gameName: details.gameName || details.game?.name || session.gameName,
        duration: details.duration ?? details.session?.duration ?? session.duration,
        cashEarned: details.cashEarned ?? details.session?.cashEarned ?? session.cashEarned,
        validationScore: details.validationScore ?? details.validation?.score ?? session.validationScore,
        validationStatus: details.validationStatus || details.validation?.status || session.validationStatus,
        validationFlags: details.validationFlags || details.validation?.flags || session.validationFlags,
        webcamChecksCount: details.webcamChecksCount ?? details.webcam?.checksCount ?? session.webcamChecksCount,
        gameplayVideoUrl: details.gameplayVideoUrl || details.evidence?.gameplayVideoUrl || session.gameplayVideoUrl,
        webcamVideoUrl: details.webcamVideoUrl || details.evidence?.webcamVideoUrl || session.webcamVideoUrl,
        faceCoverage: details.faceCoverage || details.webcam?.faceCoverage,
        fraudReason: details.fraudReason || details.review?.fraudReason || session.fraudReason,
        reviewedBy: details.reviewedBy || details.review?.reviewedBy || session.reviewedBy,
        reviewedAt: details.reviewedAt || details.review?.reviewedAt || session.reviewedAt,
        reviewNotes: details.reviewNotes || details.review?.reviewNotes || session.reviewNotes,
      });
      setReviewNotes("");
      setFraudReason("");
      setTimeout(() => {
        const row = document.querySelector(
          `.sessions-table .table-row[data-session-id="${session.sessionId}"]`,
        );
        row?.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }, 50);
    } catch (err) {
      console.error("Error fetching session details:", err);
      alert("Failed to load session details");
    } finally {
      setLoading(false);
    }
  };

  const handleReviewSession = async (decision) => {
    if (!selectedSession) return;

    try {
      setLoading(true);

      await reviewSession(selectedSession.sessionId, {
        decision,
        reviewNotes,
        fraudReason: decision === "rejected" ? fraudReason : undefined,
      });

      alert(`Session ${decision === "approved" ? "approved" : "rejected"} successfully!`);
      
      setSelectedSession(null);
      setReviewNotes("");
      setFraudReason("");
      await fetchSessionData();
    } catch (err) {
      console.error("Error reviewing session:", err);
      alert("Failed to review session");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkReviewSessions = async (decision) => {
    if (selectedSessions.length === 0) {
      alert("No sessions selected");
      return;
    }

    const reason = prompt(`Enter reason for bulk ${decision}:`);
    if (!reason) return;

    try {
      setLoading(true);

      await bulkReviewSessions({
        sessionIds: selectedSessions,
        decision,
        reason,
      });

      alert(`${selectedSessions.length} session(s) ${decision} successfully!`);
      
      setSelectedSessions([]);
      await fetchSessionData();
    } catch (err) {
      console.error("Error bulk reviewing sessions:", err);
      alert("Failed to bulk review sessions");
    } finally {
      setLoading(false);
    }
  };

  const toggleSessionSelection = (sessionId) => {
    setSelectedSessions((prev) =>
      prev.includes(sessionId)
        ? prev.filter((id) => id !== sessionId)
        : [...prev, sessionId]
    );
  };

  const selectAllSessions = () => {
    if (selectedSessions.length === sessions.length) {
      setSelectedSessions([]);
    } else {
      setSelectedSessions(sessions.map((s) => s.sessionId));
    }
  };

  // ============================================
  // PAYOUT MANAGEMENT FUNCTIONS
  // ============================================

  const fetchPayoutData = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await getAllPayouts(payoutFilter);
      setPayouts(data.payouts || []);
    } catch (err) {
      console.error("Error fetching payout data:", err);
      
      if (err.response?.status === 500 || err.code === 'ERR_BAD_RESPONSE') {
        setError("⚠️ Backend payout endpoints are not yet implemented.");
        setPayouts([]);
      } else {
        setError("Failed to load payout data");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApprovePayout = async () => {
    if (!selectedPayout) return;

    const confirm = window.confirm(
      `Approve payout of $${formatCurrency(selectedPayout.amount)} to ${selectedPayout.userName}?`
    );
    if (!confirm) return;

    try {
      setLoading(true);

      await approvePayout(selectedPayout._id, payoutNotes);
      
      alert("Payout approved and processing!");
      
      setSelectedPayout(null);
      setPayoutNotes("");
      await fetchPayoutData();
    } catch (err) {
      console.error("Error approving payout:", err);
      alert(`Failed to approve payout: ${err.response?.data?.message || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRejectPayout = async () => {
    if (!selectedPayout) return;
    if (!rejectionReason.trim()) {
      alert("Please provide a rejection reason");
      return;
    }

    try {
      setLoading(true);

      await rejectPayout(selectedPayout._id, rejectionReason);
      
      alert("Payout rejected!");
      
      setSelectedPayout(null);
      setRejectionReason("");
      await fetchPayoutData();
    } catch (err) {
      console.error("Error rejecting payout:", err);
      alert(`Failed to reject payout: ${err.response?.data?.message || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // REPORT GENERATION FUNCTIONS
  // ============================================

  const handleGenerateReport = async () => {
    const developerId = parseInt(reportDeveloperId);
    const gameId = parseInt(reportGameId);
    const buildId = parseInt(reportBuildId);

    if (!developerId || !gameId || !buildId) {
      setReportError("Please provide Developer ID, Game ID, and Build ID");
      return;
    }

    try {
      setIsGeneratingReport(true);
      setReportError(null);
      setReportResult(null);

      const result = await generateBuildReport(developerId, gameId, buildId);

      setReportResult(result);
      
      // Show success notification
      alert(`Report generated successfully!\n\nBuild: ${result.buildVersion}\nSessions Analyzed: ${result.sessionsAnalyzed}\n\nClick OK to open the report.`);
      
      // Open report in new tab
      if (result.reportUrl) {
        window.open(result.reportUrl, '_blank');
      }
    } catch (err) {
      console.error("Error generating report:", err);
      
      if (err.response?.status === 403) {
        setReportError("Access denied. You may not have permission to generate reports for this build.");
      } else if (err.response?.status === 404) {
        setReportError("Build not found. Please verify the Developer ID, Game ID, and Build ID.");
      } else if (err.response?.status === 400) {
        setReportError(err.response?.data?.message || "Invalid parameters. Please check your input.");
      } else {
        setReportError(err.response?.data?.message || err.message || "Failed to generate report. Please try again.");
      }
    } finally {
      setIsGeneratingReport(false);
    }
  };

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '0.00';
    const numAmount = typeof amount === 'number' ? amount : parseFloat(amount);
    return isNaN(numAmount) ? '0.00' : numAmount.toFixed(2);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const getScoreColor = (score) => {
    if (score < 20) return "score-low";
    if (score < 60) return "score-medium";
    return "score-high";
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending_approval: { label: "Pending", className: "status-pending" },
      approved: { label: "Approved", className: "status-approved" },
      processing: { label: "Processing", className: "status-processing" },
      completed: { label: "Completed", className: "status-completed" },
      rejected: { label: "Rejected", className: "status-rejected" },
      failed: { label: "Failed", className: "status-failed" },
    };
    return badges[status] || { label: status, className: "" };
  };

  // ============================================
  // RENDER
  // ============================================

  if (loading && activeTab !== "health" && sessions.length === 0 && payouts.length === 0) {
    return (
      <AdminLayout
        activeTab={activeTab}
        onTabChange={setActiveTab}
        flaggedCount={sessionStats?.flagged || 0}
        pendingCount={pendingGames.length}
        pendingGames={pendingGames}
        viewerRole={viewerRole}
      >
        <div className="admin-dashboard loading">
          <p>Loading admin dashboard...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      activeTab={activeTab}
      onTabChange={setActiveTab}
      flaggedCount={sessionStats?.flagged || 0}
      pendingCount={pendingGames.length}
      pendingGames={pendingGames}
      viewerRole={viewerRole}
      showRefresh={activeTab === "sessions" || activeTab === "payouts"}
      onRefresh={activeTab === "sessions" ? fetchSessionData : fetchPayoutData}
    >
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <h1>Admin Dashboard</h1>
        {activeTab !== 'assignments' && (
          <button 
            className="refresh-button" 
            onClick={activeTab === 'sessions' ? fetchSessionData : fetchPayoutData}
            disabled={loading}
          >
            🔄 Refresh
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="dashboard-tabs">
        <button
          className={`tab ${activeTab === 'sessions' ? 'active' : ''}`}
          onClick={() => setActiveTab('sessions')}
        >
          🛡️ Session Review
          {sessionStats && sessionStats.flagged > 0 && (
            <span className="tab-badge">{sessionStats.flagged}</span>
          )}
        </button>
        <button
          className={`tab ${activeTab === 'payouts' ? 'active' : ''}`}
          onClick={() => setActiveTab('payouts')}
        >
          💰 Payout Management
        </button>
        <button
          className={`tab ${activeTab === 'assignments' ? 'active' : ''}`}
          onClick={() => setActiveTab('assignments')}
        >
          👥 Manual Assignment
        </button>
        <button
          className={`tab ${activeTab === 'game-assignments' ? 'active' : ''}`}
          onClick={() => setActiveTab('game-assignments')}
        >
          🎯 Game Assignments
        </button>
        <button
          className={`tab ${activeTab === 'reports' ? 'active' : ''}`}
          onClick={() => setActiveTab('reports')}
        >
          📊 Feedback Reports
        </button>
      </div>

      {error && (
        <div className="error-banner">
          <strong>⚠️ Backend Not Implemented:</strong> {error}
        </div>
      )}

      {activeTab === "health" && (
        <div className="tab-content">
          <SystemHealth />
        </div>
      )}

      {/* Session Review Tab */}
      {activeTab === 'sessions' && (
        <div className={`tab-content sessions-tab${selectedSession ? " has-review" : ""}`}>
          {/* Session Filters */}
          <div className="session-filters">
            <button 
              className={`filter-btn ${sessionFilter === 'flagged' ? 'active' : ''}`}
              onClick={() => setSessionFilter('flagged')}
            >
              ⚠️ Flagged ({sessionStats?.flagged || 0})
            </button>
            <button 
              className={`filter-btn ${sessionFilter === 'rejected' ? 'active' : ''}`}
              onClick={() => setSessionFilter('rejected')}
            >
              ❌ Rejected ({sessionStats?.rejected || 0})
            </button>
            <button 
              className={`filter-btn ${sessionFilter === 'approved' ? 'active' : ''}`}
              onClick={() => setSessionFilter('approved')}
            >
              ✅ Approved ({(sessionStats?.approved || 0) + (sessionStats?.auto_approved || 0)})
            </button>
            <button 
              className={`filter-btn ${sessionFilter === 'pending' ? 'active' : ''}`}
              onClick={() => setSessionFilter('pending')}
            >
              ⏳ Pending ({sessionStats?.pending || 0})
            </button>
            <button 
              className={`filter-btn ${sessionFilter === 'all' ? 'active' : ''}`}
              onClick={() => setSessionFilter('all')}
            >
              📋 All ({sessionStats?.total || 0})
            </button>
          </div>

          {/* Session Stats */}
          {sessionStats && (
            <div className="dashboard-stats">
              <div className="stat-card stat-flagged">
                <div className="stat-value">{sessionStats.flagged}</div>
                <div className="stat-label">Flagged</div>
              </div>
              <div className="stat-card stat-pending">
                <div className="stat-value">{sessionStats.pending}</div>
                <div className="stat-label">Pending</div>
              </div>
              <div className="stat-card stat-approved">
                <div className="stat-value">{sessionStats.auto_approved + sessionStats.approved}</div>
                <div className="stat-label">Approved</div>
              </div>
              <div className="stat-card stat-rejected">
                <div className="stat-value">{sessionStats.rejected}</div>
                <div className="stat-label">Rejected</div>
              </div>
              <div className="stat-card stat-total">
                <div className="stat-value">{sessionStats.total}</div>
                <div className="stat-label">Total</div>
              </div>
            </div>
          )}

          <div className={`dashboard-content${selectedSession ? " has-review" : ""}`}>
            <div className="sessions-panel">
              <div className="panel-header">
                <h2>
                  {sessionFilter === 'flagged' && `⚠️ Flagged Sessions (${sessions.length})`}
                  {sessionFilter === 'rejected' && `❌ Rejected Sessions (${sessions.length})`}
                  {sessionFilter === 'approved' && `✅ Approved Sessions (${sessions.length})`}
                  {sessionFilter === 'pending' && `⏳ Pending Sessions (${sessions.length})`}
                  {sessionFilter === 'all' && `📋 All Sessions (${sessions.length})`}
                </h2>
                {selectedSessions.length > 0 && (
                  <div className="bulk-actions">
                    <span>{selectedSessions.length} selected</span>
                    <button onClick={() => handleBulkReviewSessions("approved")} className="btn-approve">
                      ✅ Approve All
                    </button>
                    <button onClick={() => handleBulkReviewSessions("rejected")} className="btn-reject">
                      ❌ Reject All
                    </button>
                  </div>
                )}
              </div>

              <div className="sessions-table">
                <div className="table-header">
                  <div className="col-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedSessions.length === sessions.length && sessions.length > 0}
                      onChange={selectAllSessions}
                    />
                  </div>
                  <div className="col-player">Player</div>
                  <div className="col-game">Game</div>
                  <div className="col-duration">Duration</div>
                  <div className="col-score">Score</div>
                  <div className="col-date">Date</div>
                  <div className="col-actions">Actions</div>
                </div>

                <div className="table-body">
                  {sessions.length === 0 ? (
                    <div className="no-data">
                      {sessionFilter === 'flagged' && <p>🎉 No flagged sessions! Everything looks good.</p>}
                      {sessionFilter === 'rejected' && <p>📭 No rejected sessions found.</p>}
                      {sessionFilter === 'approved' && <p>📭 No approved sessions found.</p>}
                      {sessionFilter === 'pending' && <p>⏳ No pending sessions (awaiting evidence upload).</p>}
                      {sessionFilter === 'all' && <p>📭 No sessions found.</p>}
                    </div>
                  ) : (
                    sessions.map((session) => (
                      <div
                        key={session.sessionId}
                        data-session-id={session.sessionId}
                        className={`table-row${selectedSession?.sessionId === session.sessionId ? " reviewing" : ""}`}
                      >
                        <div className="col-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedSessions.includes(session.sessionId)}
                            onChange={() => toggleSessionSelection(session.sessionId)}
                          />
                        </div>
                        <div className="col-player">
                          <div className="player-name">{session.playtesterName}</div>
                          {session.playtesterUsername && (
                            <div className="player-username">@{session.playtesterUsername}</div>
                          )}
                          <div className="player-id">ID: {session.playtesterId}</div>
                          {session.validationStatus && (
                            <div className={`status-badge-inline status-${session.validationStatus}`}>
                              {session.validationStatus}
                            </div>
                          )}
                        </div>
                        <div className="col-game">{session.gameName}</div>
                        <div className="col-duration">{formatDuration(session.duration)}</div>
                        <div className="col-score">
                          <span className={`score-badge ${getScoreColor(session.validationScore)}`}>
                            {session.validationScore}
                          </span>
                        </div>
                        <div className="col-date">{formatDate(session.sessionStart)}</div>
                        <div className="col-actions">
                          <button
                            className="btn-review"
                            onClick={() => handleSelectSession(session)}
                          >
                            Review
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Session Review Panel */}
            {selectedSession && (
              <div className="review-panel">
                <div className="panel-header">
                  <h2>Review Session #{selectedSession.sessionId}</h2>
                  <button className="close-button" onClick={() => setSelectedSession(null)}>
                    ✕
                  </button>
                </div>

                <div className="session-details">
                  <div className="detail-section">
                    <h3>Session Information</h3>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <span className="detail-label">Player Name:</span>
                        <span className="detail-value">{selectedSession.playtesterName}</span>
                      </div>
                      {selectedSession.playtesterUsername && (
                        <div className="detail-item">
                          <span className="detail-label">Username:</span>
                          <span className="detail-value username-value">@{selectedSession.playtesterUsername}</span>
                        </div>
                      )}
                      <div className="detail-item">
                        <span className="detail-label">Player ID:</span>
                        <span className="detail-value">{selectedSession.playtesterId}</span>
                      </div>
                      {selectedSession.playtesterEmail && (
                        <div className="detail-item">
                          <span className="detail-label">Email:</span>
                          <span className="detail-value email-value">{selectedSession.playtesterEmail}</span>
                        </div>
                      )}
                      <div className="detail-item">
                        <span className="detail-label">Game:</span>
                        <span className="detail-value">{selectedSession.gameName}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Duration:</span>
                        <span className="detail-value">
                          {formatDuration(selectedSession.duration)}
                        </span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Cash Earned:</span>
                        <span className="detail-value">${formatCurrency(selectedSession.cashEarned)}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Validation Score:</span>
                        <span className={`detail-value ${getScoreColor(selectedSession.validationScore)}`}>
                          {selectedSession.validationScore}
                        </span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Webcam Checks:</span>
                        <span className="detail-value">{selectedSession.webcamChecksCount}</span>
                      </div>
                      {selectedSession.evidence?.bucketFolder && (
                        <div className="detail-item full-width">
                          <span className="detail-label">Bucket folder:</span>
                          <span className="detail-value">{selectedSession.evidence.bucketFolder}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {selectedSession.faceCoverage && (
                    <div className="detail-section">
                      <h3>MorphCast Face Coverage</h3>
                      <div className={`face-coverage ${selectedSession.faceCoverage.passes ? "pass" : "fail"}`}>
                        <div className="face-coverage-score">
                          {selectedSession.faceCoverage.source === "missing"
                            ? "No MorphCast log"
                            : `${selectedSession.faceCoverage.percent}% face on camera`}
                        </div>
                        <p>
                          {selectedSession.faceCoverage.note ||
                            `Need at least ${selectedSession.faceCoverage.threshold || 75}% of session length.`}
                        </p>
                        {selectedSession.faceCoverage.source !== "missing" && (
                          <p className="face-coverage-meta">
                            {selectedSession.faceCoverage.presentSeconds}s present /
                            {" "}{selectedSession.faceCoverage.durationSeconds}s session
                            {" "}· {selectedSession.faceCoverage.sampleCount} MorphCast samples
                            {selectedSession.faceCoverage.clamped ? " · clamped to 0–100%" : ""}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Rejection Details Section */}
                  {selectedSession.validationStatus === 'rejected' && (
                    <div className="detail-section rejection-section">
                      <h3>🚫 Rejection Information</h3>
                      <div className="rejection-details">
                        <div className="detail-item">
                          <span className="detail-label">Rejection Reason:</span>
                          <span className="detail-value rejection-reason">
                            {selectedSession.fraudReason || "No reason provided"}
                          </span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Reviewed By:</span>
                          <span className="detail-value">
                            {selectedSession.reviewedBy || "Unknown"}
                          </span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Reviewed At:</span>
                          <span className="detail-value">
                            {selectedSession.reviewedAt 
                              ? formatDate(selectedSession.reviewedAt)
                              : "Unknown"}
                          </span>
                        </div>
                        {selectedSession.reviewNotes && (
                          <div className="detail-item full-width">
                            <span className="detail-label">Review Notes:</span>
                            <span className="detail-value review-notes">
                              {selectedSession.reviewNotes}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {selectedSession.validationFlags && selectedSession.validationFlags.length > 0 && (
                    <div className="detail-section">
                      <h3>Validation Flags</h3>
                      <ul className="flags-list">
                        {selectedSession.validationFlags.map((flag, idx) => (
                          <li key={idx} className="flag-item">
                            ⚠️ {flag}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="detail-section">
                    <h3>Evidence Samples</h3>
                    <p className="sample-help">
                      Opening a session generates review stills (start, middle, end) from the gameplay and webcam videos. Click a still to jump in the player, or open the full video.
                    </p>
                    <EvidenceSampleStrip
                      sessionId={selectedSession.sessionId}
                      kind="gameplay"
                      src={selectedSession.gameplayVideoUrl}
                      label="Gameplay"
                      durationSeconds={Number(selectedSession.duration || 0) * 60}
                    />
                    <EvidenceSampleStrip
                      sessionId={selectedSession.sessionId}
                      kind="webcam"
                      src={selectedSession.webcamVideoUrl}
                      label="Webcam"
                      durationSeconds={Number(selectedSession.duration || 0) * 60}
                    />
                    <div className="video-links">
                      {selectedSession.gameplayVideoUrl ? (
                        <a
                          href={selectedSession.gameplayVideoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="video-link"
                        >
                          Open full gameplay
                        </a>
                      ) : (
                        <span className="video-missing">No gameplay video in bucket</span>
                      )}
                      {selectedSession.webcamVideoUrl ? (
                        <a
                          href={selectedSession.webcamVideoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="video-link"
                        >
                          Open full webcam
                        </a>
                      ) : (
                        <span className="video-missing">No webcam video in bucket</span>
                      )}
                    </div>
                  </div>

                  <div className="detail-section">
                    <h3>Review Notes</h3>
                    <textarea
                      className="review-textarea"
                      value={reviewNotes}
                      onChange={(e) => setReviewNotes(e.target.value)}
                      placeholder="Enter your review notes here..."
                      rows="4"
                    />
                  </div>

                  <div className="detail-section">
                    <h3>Fraud Reason (if rejecting)</h3>
                    <textarea
                      className="review-textarea"
                      value={fraudReason}
                      onChange={(e) => setFraudReason(e.target.value)}
                      placeholder="Enter reason for rejection..."
                      rows="3"
                    />
                  </div>

                  <div className="review-actions">
                    <button
                      className="btn-approve-large"
                      onClick={() => handleReviewSession("approved")}
                      disabled={loading}
                    >
                      ✅ Approve Session
                    </button>
                    <button
                      className="btn-reject-large"
                      onClick={() => handleReviewSession("rejected")}
                      disabled={loading}
                    >
                      ❌ Reject Session
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Payout Management Tab */}
      {activeTab === 'payouts' && (
        <div className="tab-content">
          {/* Payout Filters */}
          <div className="payout-filters">
            <label>Filter by status:</label>
            <select value={payoutFilter} onChange={(e) => setPayoutFilter(e.target.value)}>
              <option value="pending_approval">Pending Approval</option>
              <option value="approved">Approved</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
              <option value="rejected">Rejected</option>
              <option value="failed">Failed</option>
              <option value="">All</option>
            </select>
          </div>

          <div className="dashboard-content">
            <div className="payouts-panel">
              <div className="panel-header">
                <h2>Payout Requests ({payouts.length})</h2>
              </div>

              <div className="payouts-table">
                <div className="table-header">
                  <div className="col-user">User</div>
                  <div className="col-amount">Amount</div>
                  <div className="col-method">Method</div>
                  <div className="col-status">Status</div>
                  <div className="col-date">Requested</div>
                  <div className="col-actions">Actions</div>
                </div>

                {payouts.length === 0 ? (
                  <div className="no-data">
                    <p>No payout requests found.</p>
                  </div>
                ) : (
                  payouts.map((payout) => (
                    <div key={payout._id} className="table-row">
                      <div className="col-user">
                        <div className="user-name">{payout.userName || 'Unknown User'}</div>
                        <div className="user-id">ID: {payout.userId}</div>
                      </div>
                      <div className="col-amount">
                        <strong>${formatCurrency(payout.amount)}</strong>
                        <span className="currency">{payout.currency || 'USD'}</span>
                      </div>
                      <div className="col-method">
                        {payout.payoutProvider === 'wise' ? '🏦 Wise' : '💳 PayPal'}
                      </div>
                      <div className="col-status">
                        <span className={`status-badge ${getStatusBadge(payout.status).className}`}>
                          {getStatusBadge(payout.status).label}
                        </span>
                      </div>
                      <div className="col-date">{formatDate(payout.requestedAt)}</div>
                      <div className="col-actions">
                        {payout.status === 'pending_approval' && (
                          <button
                            className="btn-review"
                            onClick={() => setSelectedPayout(payout)}
                          >
                            Review
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Payout Review Panel */}
            {selectedPayout && (
              <div className="review-panel">
                <div className="panel-header">
                  <h2>Review Payout Request</h2>
                  <button className="close-button" onClick={() => setSelectedPayout(null)}>
                    ✕
                  </button>
                </div>

                <div className="payout-details">
                  <div className="detail-section">
                    <h3>Payout Information</h3>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <span className="detail-label">User:</span>
                        <span className="detail-value">{selectedPayout.userName || 'Unknown'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">User ID:</span>
                        <span className="detail-value">{selectedPayout.userId}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Amount:</span>
                        <span className="detail-value">
                          ${formatCurrency(selectedPayout.amount)} {selectedPayout.currency || 'USD'}
                        </span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Provider:</span>
                        <span className="detail-value">{selectedPayout.payoutProvider}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Requested:</span>
                        <span className="detail-value">{formatDate(selectedPayout.requestedAt)}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Status:</span>
                        <span className={`detail-value ${getStatusBadge(selectedPayout.status).className}`}>
                          {getStatusBadge(selectedPayout.status).label}
                        </span>
                      </div>
                    </div>
                  </div>

                  {selectedPayout.payoutProvider === 'wise' && selectedPayout.wiseRecipientId && (
                    <div className="detail-section">
                      <h3>Wise Details</h3>
                      <p><strong>Recipient ID:</strong> {selectedPayout.wiseRecipientId}</p>
                    </div>
                  )}

                  {selectedPayout.payoutProvider === 'paypal' && selectedPayout.paypalEmail && (
                    <div className="detail-section">
                      <h3>PayPal Details</h3>
                      <p><strong>Email:</strong> {selectedPayout.paypalEmail}</p>
                    </div>
                  )}

                  <div className="detail-section">
                    <h3>Admin Notes (optional)</h3>
                    <textarea
                      className="review-textarea"
                      value={payoutNotes}
                      onChange={(e) => setPayoutNotes(e.target.value)}
                      placeholder="Enter optional notes..."
                      rows="3"
                    />
                  </div>

                  <div className="detail-section">
                    <h3>Rejection Reason (if rejecting)</h3>
                    <textarea
                      className="review-textarea"
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      placeholder="Enter reason for rejection..."
                      rows="3"
                    />
                  </div>

                  <div className="review-actions">
                    <button
                      className="btn-approve-large"
                      onClick={handleApprovePayout}
                      disabled={loading}
                    >
                      ✅ Approve Payout
                    </button>
                    <button
                      className="btn-reject-large"
                      onClick={handleRejectPayout}
                      disabled={loading || !rejectionReason.trim()}
                    >
                      ❌ Reject Payout
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Manual Assignment Tab */}
      {activeTab === 'assignments' && (
        <div className="tab-content">
          <ManualAssignment onAssignmentComplete={fetchPendingAssignments} />
        </div>
      )}

      {activeTab === 'studios' && <AdminDeveloperStudios />}

      {/* Game Assignments Manager Tab */}
      {activeTab === 'game-assignments' && (
        <div className="tab-content">
          <GameAssignmentsManager />
        </div>
      )}

      {activeTab === 'reports' && (
        <div className="tab-content">
          <div className="reports-panel">
            <div className="panel-header">
              <h2>📊 Generate Feedback Report</h2>
              <p className="panel-description">
                Generate comprehensive feedback reports for game builds, combining emotional data (face annotations) and text feedback from playtesters.
              </p>
            </div>

            <div className="report-form">
              <div className="form-section">
                <h3>Build Information</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="developerId">Developer ID *</label>
                    <input
                      type="number"
                      id="developerId"
                      value={reportDeveloperId}
                      onChange={(e) => setReportDeveloperId(e.target.value)}
                      placeholder="e.g., 784"
                      disabled={isGeneratingReport}
                    />
                    <small>The developer's user ID</small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="gameId">Game ID *</label>
                    <input
                      type="number"
                      id="gameId"
                      value={reportGameId}
                      onChange={(e) => setReportGameId(e.target.value)}
                      placeholder="e.g., 45"
                      disabled={isGeneratingReport}
                    />
                    <small>The game ID</small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="buildId">Build ID *</label>
                    <input
                      type="number"
                      id="buildId"
                      value={reportBuildId}
                      onChange={(e) => setReportBuildId(e.target.value)}
                      placeholder="e.g., 123"
                      disabled={isGeneratingReport}
                    />
                    <small>The build ID to generate report for</small>
                  </div>
                </div>
              </div>

              {reportError && (
                <div className="error-message" style={{ 
                  padding: '12px', 
                  marginBottom: '20px', 
                  backgroundColor: '#fee', 
                  border: '1px solid #fcc',
                  borderRadius: '4px',
                  color: '#c33'
                }}>
                  ⚠️ {reportError}
                </div>
              )}

              {reportResult && (
                <div className="success-message" style={{ 
                  padding: '12px', 
                  marginBottom: '20px', 
                  backgroundColor: '#efe', 
                  border: '1px solid #cfc',
                  borderRadius: '4px',
                  color: '#3c3'
                }}>
                  ✅ Report Generated Successfully!
                  <div style={{ marginTop: '8px', fontSize: '14px' }}>
                    <div><strong>Build:</strong> {reportResult.buildVersion}</div>
                    <div><strong>Sessions Analyzed:</strong> {reportResult.sessionsAnalyzed}</div>
                    <div><strong>Generated At:</strong> {formatDate(reportResult.generatedAt)}</div>
                    {reportResult.reportUrl && (
                      <div style={{ marginTop: '8px' }}>
                        <a 
                          href={reportResult.reportUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ color: '#0066cc', textDecoration: 'underline' }}
                        >
                          📄 View Report
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="form-actions">
                <button
                  className="btn-generate-report"
                  onClick={handleGenerateReport}
                  disabled={isGeneratingReport || !reportDeveloperId || !reportGameId || !reportBuildId}
                  style={{
                    padding: '12px 24px',
                    fontSize: '16px',
                    backgroundColor: isGeneratingReport ? '#ccc' : '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: isGeneratingReport ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  {isGeneratingReport ? (
                    <>
                      <span>⏳</span>
                      Generating Report...
                    </>
                  ) : (
                    <>
                      <span>📊</span>
                      Generate Feedback Report
                    </>
                  )}
                </button>
              </div>

              {isGeneratingReport && (
                <div className="generation-progress" style={{ 
                  marginTop: '20px', 
                  padding: '16px', 
                  backgroundColor: '#16151f', 
                  borderRadius: '4px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '18px', marginBottom: '8px' }}>⏳</div>
                  <div><strong>Generating Report</strong></div>
                  <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                    Analyzing playtester feedback and emotional data...
                  </div>
                  <div style={{ fontSize: '12px', color: '#999', marginTop: '8px' }}>
                    This typically takes 5-30 seconds depending on the number of sessions.
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
    </AdminLayout>
  );
};

export default AdminDashboard;

